import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { createPortal } from 'react-dom';
import { FiDownload, FiPrinter, FiX, FiEdit2 } from 'react-icons/fi';
import html2pdf from 'html2pdf.js';
import '../styles/ViewInvoice.css';
import { graphqlRequest } from '../utils/graphqlRequest';
import { formatAUD } from '../utils/formatCurrency';
import {
  useProfileProjectId,
} from './ProfilePages/ProfileBookingContext';
import {
  ensureInvoiceHasBaseServices,
  hydrateInvoiceWithTasks,
  invoiceNeedsTaskHydration,
  keepSelectedProjectTasks,
  mergeVisitExtrasIntoInvoice,
} from '../utils/invoiceTaskUtils';
import {
  inferInvoiceType,
  calcInvoiceTotals,
  gstLineLabel,
  grandTotalLabel,
} from '../utils/invoiceTax';

const FIND_TASKS_BY_PROJECT = `
  query FindTasksByProject($projectId: String!) {
    findTasksByProject(projectId: $projectId) {
      id
      title
      description
      remainder
      project {
        id
      }
    }
  }
`;

const FIND_PROJECT_SERVICES = `
  query FindOneProject($id: String!) {
    findOneProject(id: $id) {
      id
      seriesRootId
      projectServices
      visitExtras
      attention
      projectAddress
      deliveryAddress
      purchaseOrderNumber
      booking {
        id
        address
      }
    }
  }
`;

const FIND_INVOICE_CLIENT = `
  query FindInvoiceClient($id: String!) {
    findOneBooking(id: $id) {
      id
      fullName
      email
      phone
      address
      postcode
    }
  }
`;

const FIND_LEAD_PROJECT_BILLING = `
  query FindBookingProjectBilling($bookingId: String!) {
    findBookingProject(bookingId: $bookingId) {
      id
      seriesRootId
      purchaseOrderNumber
      projectAddress
      deliveryAddress
      attention
    }
  }
`;

const GET_COMPANY_INVOICE_BANK = `
  query GetCompanyInvoiceBankForView {
    getMyProfile {
      company {
        companyName
        abn
        unitNumber
        address
        suburb
        state
        postCode
        country
        companyPhone
        companyEmail
        invoicingEmail
        invoiceBankName
        invoiceAccountName
        invoiceBsb
        invoiceAccountNumber
        invoiceBankReference
      }
    }
  }
`;

const GET_INVOICE_SERVICE_TEXT = `
  query InvoiceViewServiceCatalog {
    getService(includeStats: false) {
      id
      service
      description
      catalog
    }
  }
`;

const UPDATE_PAYMENT_STATUS_BY_INVOICE = `
  mutation UpdateInvoicePaymentStatus(
    $invoiceId: String!
    $paymentStatus: PaymentStatus!
  ) {
    updateInvoicePaymentStatus(
      invoiceId: $invoiceId
      paymentStatus: $paymentStatus
    )
  }
`;

const UPDATE_PAYMENT_STATUS_BY_BOOKING = `
  mutation UpdateInvoiceStatusByBooking(
    $bookingId: String!
    $paymentStatus: PaymentStatus!
  ) {
    updateInvoiceStatusByBooking(
      bookingId: $bookingId
      paymentStatus: $paymentStatus
    )
  }
`;

export const PaymentStatus = {
  PAID: 'PAID',
  UNPAID: 'UNPAID',
};

function displayValue(val) {
  if (val == null || val === '' || val === 'null' || val === 'undefined') {
    return '—';
  }
  return String(val);
}

function labeledLine(text, label) {
  const match = String(text || '').match(
    new RegExp(`^${label}:\\s*(.+)$`, 'im'),
  );
  return match ? String(match[1]).trim() : '';
}

function taskBody(task) {
  const title = String(task?.title || task?.taskTitle || task?.name || '')
    .trim()
    .toLowerCase();
  const body = String(task?.description || task?.taskDescription || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(
      (line) =>
        line &&
        !/^Service:/i.test(line) &&
        !/^Requirement:/i.test(line) &&
        !/^Role:/i.test(line) &&
        !/^Frequency:/i.test(line) &&
        !/^Group:/i.test(line) &&
        !/^Client-facing scope:/i.test(line) &&
        !/^Internal work instructions:/i.test(line) &&
        !/^Source:/i.test(line),
    )
    .join('\n')
    .trim();
  if (!body || body.toLowerCase() === title) return '';
  return body;
}

function taskFacing(task) {
  const text = task?.description || task?.taskDescription || '';
  return {
    groupName: String(task?.groupName || '').trim() || labeledLine(text, 'Group'),
    scope:
      String(task?.scope || '').trim() ||
      labeledLine(text, 'Client-facing scope') ||
      taskBody(task),
    instructions:
      String(task?.instructions || '').trim() ||
      labeledLine(text, 'Internal work instructions'),
  };
}

function rememberFacing(map, key, facing) {
  const id = String(key || '').trim().toLowerCase();
  if (!id || !facing) return;
  const prev = map.get(id) || {
    label: '',
    groupName: '',
    scope: '',
    instructions: '',
  };
  map.set(id, {
    label: prev.label || facing.label || '',
    groupName: prev.groupName || facing.groupName,
    scope: prev.scope || facing.scope,
    instructions: prev.instructions || facing.instructions,
  });
}

function facingIndex(tasks) {
  const map = new Map();
  (Array.isArray(tasks) ? tasks : []).forEach((task) => {
    const facing = taskFacing(task);
    const label = String(task?.title || task?.name || task?.taskTitle || '').trim();
    rememberFacing(map, label, { ...facing, label });
    rememberFacing(map, task?.id ? `id:${task.id}` : '', facing);
    rememberFacing(map, task?.taskId ? `id:${task.taskId}` : '', facing);
  });
  return map;
}

function facingForTask(index, task) {
  if (!index) return null;
  const id = String(task?.taskId || task?.id || '').trim();
  const title = String(task?.title || task?.taskTitle || task?.name || '')
    .trim()
    .toLowerCase();
  return index.get(id ? `id:${id}`.toLowerCase() : '') || index.get(title) || null;
}

function catalogFacingIndex(service) {
  const catalog = service?.catalog || {};
  const groups = [
    ...(Array.isArray(catalog.groups) ? catalog.groups : []),
    ...(Array.isArray(catalog.tiers) ? catalog.tiers : []).flatMap(
      (tier) => tier?.groups || [],
    ),
  ];
  const tasks = groups.flatMap((group) =>
    (Array.isArray(group?.tasks) ? group.tasks : []).map((task) => ({
      ...task,
      groupName: task?.groupName || group?.name || group?.title || '',
    })),
  );
  return facingIndex(tasks);
}

function indexForGroup(indexes, group) {
  const keys = [
    group?.serviceId,
    group?.service?.id,
    group?.serviceName,
    group?.service?.service,
    group?.name,
  ];
  for (const key of keys) {
    const text = String(key || '').trim();
    if (!text) continue;
    const hit = indexes.get(text) || indexes.get(text.toLowerCase());
    if (hit) return hit;
  }
  return null;
}

function withTaskFacing(invoice, indexes) {
  const groups = invoice?.services?.groups;
  if (!Array.isArray(groups)) return invoice;
  return {
    ...invoice,
    services: {
      ...invoice.services,
      groups: groups.map((group) => {
        const found = indexForGroup(indexes, group);
        return {
          ...group,
          serviceDescription:
            String(group?.serviceDescription || '').trim() ||
            found?.description ||
            '',
          tasks: (() => {
            const tasks = (Array.isArray(group.tasks) ? group.tasks : []).map(
              (task) => {
                const own = taskFacing(task);
                const extra = facingForTask(found?.tasks, task);
                // Invoice = client document: no internal work instructions.
                const { instructions: _internal, ...clientTask } = task || {};
                return {
                  ...clientTask,
                  groupName: own.groupName || extra?.groupName || '',
                  scope: own.scope || extra?.scope || '',
                };
              },
            );
            return tasks;
          })(),
        };
      }),
    },
  };
}

async function facingIndexesFor(invoice, projectServices) {
  const indexes = new Map();
  const remember = (serviceId, tasks, description = '') => {
    const id = String(serviceId || '').trim();
    if (!id) return;
    const name = String(serviceId || '').trim();
    const record = {
      description: String(description || '').trim(),
      tasks: facingIndex(tasks),
    };
    indexes.set(id, mergeFacingRecord(indexes.get(id), record));
    if (name && name !== id) {
      indexes.set(name.toLowerCase(), mergeFacingRecord(indexes.get(name.toLowerCase()), record));
    }
  };

  (Array.isArray(projectServices) ? projectServices : []).forEach((line) => {
    const label = line?.service?.service || line?.serviceName || line?.name || '';
    remember(line?.service?.id || line?.serviceId, line?.tasks || line?.extra?.tasks || [], line?.description || '');
    if (label) remember(label, line?.tasks || line?.extra?.tasks || [], line?.description || '');
  });

  try {
    const res = await graphqlRequest(GET_INVOICE_SERVICE_TEXT);
    const rows = Array.isArray(res?.getService) ? res.getService : [];
    rows.forEach((service) => {
      const catalogIndex = catalogFacingIndex(service);
      const record = {
        description: String(service?.description || '').trim(),
        tasks: catalogIndex,
      };
      const id = String(service?.id || '').trim();
      const name = String(service?.service || '').trim();
      if (id) indexes.set(id, mergeFacingRecord(indexes.get(id), record));
      if (name) {
        indexes.set(name, mergeFacingRecord(indexes.get(name), record));
        indexes.set(
          name.toLowerCase(),
          mergeFacingRecord(indexes.get(name.toLowerCase()), record),
        );
      }
    });
  } catch {
    // Catalogue text is optional. Saved invoice text still shows.
  }
  return indexes;
}

function mergeFacingRecord(prev, incoming) {
  const tasks = prev?.tasks || new Map();
  (incoming?.tasks || new Map()).forEach((value, key) => {
    rememberFacing(tasks, key, value);
  });
  return {
    description: prev?.description || incoming?.description || '',
    tasks,
  };
}

function filledText(value) {
  const text = String(value ?? '').trim();
  if (!text || text === 'null' || text === 'undefined') return '';
  return text;
}

function mergeCompany(stored, live) {
  const pick = (key) => filledText(stored?.[key]) || filledText(live?.[key]);
  return {
    ...(live || {}),
    ...(stored || {}),
    companyName: pick('companyName'),
    abn: pick('abn'),
    unitNumber: pick('unitNumber'),
    address: pick('address'),
    suburb: pick('suburb'),
    state: pick('state'),
    postCode: pick('postCode'),
    country: pick('country'),
    companyPhone: pick('companyPhone'),
    companyEmail: pick('companyEmail'),
    invoicingEmail: pick('invoicingEmail'),
  };
}

function buildCompanyAddress(company) {
  if (!company) return '—';
  if (company.address && !company.suburb && !company.postCode) {
    return company.address;
  }
  const parts = [
    company.unitNumber,
    company.address,
    company.suburb,
    [company.state, company.postCode].filter(Boolean).join(' '),
    company.country,
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
}

function fmtDate(value) {
  if (!value) return '—';
  const raw = String(value).trim();
  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = ymd
    ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
    : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function addDays(date, days) {
  const raw = date != null ? String(date).trim() : '';
  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = ymd
    ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
    : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
}

function startOfDay(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const raw = String(value ?? '').trim();
  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = ymd
    ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
    : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function paymentDueSentence(invoiceDate, due) {
  const start = startOfDay(invoiceDate);
  const end = startOfDay(due);
  if (!start || !end) {
    return 'Your payment is due as shown above. Thank you for your business.';
  }
  const days = Math.round((end.getTime() - start.getTime()) / 86400000);
  if (days <= 0) {
    return 'Your payment is due on the invoice date. Thank you for your business.';
  }
  const count = days === 1 ? '1 day' : `${days} days`;
  return `Your payment is due within ${count} of the invoice date. Thank you for your business.`;
}

function formatFrequency(freq) {
  if (!freq) return 'One-time';
  const normalized = String(freq).replace(/_/g, ' ').toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function groupPricing(group) {
  const tasks = Array.isArray(group?.tasks) ? group.tasks : [];
  const priced = tasks
    .map((t) => Number(t.price) || Number(t.total) || 0)
    .filter((p) => p > 0);
  const sameTaskPrice =
    priced.length > 1 && priced.every((p) => p === priced[0]);
  const byTask =
    group?.pricingMode === 'task' ||
    (group?.pricingMode !== 'service' &&
      priced.length >= 1 &&
      new Set(priced).size > 1);
  const serviceAmount =
    Number(group?.serviceTotal) ||
    Number(group?.servicePrice) ||
    (sameTaskPrice && !byTask ? priced[0] : 0) ||
    0;
  const taskSum = tasks.reduce(
    (s, t) => s + (Number(t.total) || Number(t.price) || 0),
    0,
  );
  return {
    tasks,
    byTask,
    amount: byTask ? taskSum : serviceAmount,
    discount: Number(group?.serviceDiscount) || 0,
    frequency: formatFrequency(group?.frequency),
    name: group?.serviceName || group?.service?.service || 'Service',
  };
}

function fillInvoiceBillingFromProject(invoice, project, extra = {}) {
  if (!invoice) return invoice;
  const pick = (...values) => {
    for (const value of values) {
      const text = String(value || '').trim();
      if (text && text !== 'null' && text !== 'undefined') return text;
    }
    return null;
  };
  return {
    ...invoice,
    attention: pick(
      invoice.attention,
      project?.attention,
      extra.attention,
    ),
    projectAddress: pick(
      invoice.projectAddress,
      project?.projectAddress,
      extra.projectAddress,
      project?.booking?.address,
      invoice.bookingInfo?.address,
    ),
    deliveryAddress: pick(
      invoice.deliveryAddress,
      project?.deliveryAddress,
      extra.deliveryAddress,
    ),
    purchaseOrderNumber: pick(
      invoice.purchaseOrderNumber,
      invoice.poNumber,
      project?.purchaseOrderNumber,
      extra.purchaseOrderNumber,
    ),
  };
}

function billingFromLeadProjects(rows, projectId) {
  const list = Array.isArray(rows) ? rows : [];
  const id = String(projectId || '');
  const match = id ? list.find((row) => String(row?.id) === id) : null;
  const rootId = String(match?.seriesRootId || '').trim();
  const root = rootId
    ? list.find((row) => String(row?.id) === rootId)
    : null;
  const withPo = list.find((row) =>
    String(row?.purchaseOrderNumber || '').trim(),
  );
  const pick = (...values) => {
    for (const value of values) {
      const text = String(value || '').trim();
      if (text) return text;
    }
    return '';
  };
  return {
    purchaseOrderNumber: pick(
      match?.purchaseOrderNumber,
      root?.purchaseOrderNumber,
      withPo?.purchaseOrderNumber,
    ),
    projectAddress: pick(
      match?.projectAddress,
      root?.projectAddress,
      withPo?.projectAddress,
    ),
    deliveryAddress: pick(
      match?.deliveryAddress,
      root?.deliveryAddress,
    ),
    attention: pick(match?.attention, root?.attention),
  };
}

export default function ViewInvoice({
  invoice,
  bookingId: bookingIdProp,
  onClose,
  onPaymentStatusChange,
  onEdit,
  autoDownload = false,
  onPdf,
}) {
  const contextProjectId = useProfileProjectId();
  const slipRef = useRef(null);
  const autoDone = useRef(false);
  const [displayInvoice, setDisplayInvoice] = useState(invoice);
  const [companyBankFallback, setCompanyBankFallback] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);

  useEffect(() => {
    setDisplayInvoice(
      fillInvoiceBillingFromProject(invoice, null, {
        projectAddress: invoice?.bookingInfo?.address,
      }),
    );
  }, [invoice]);

  useEffect(() => {
    const projectId = invoice?.projectId || contextProjectId || null;
    const bookingId =
      bookingIdProp ||
      invoice?.bookingId ||
      invoice?.bookingInfo?.id ||
      null;
    if (!invoice) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const billingRes = bookingId
          ? await graphqlRequest(FIND_LEAD_PROJECT_BILLING, {
              bookingId,
            }).catch(() => ({}))
          : {};
        const leadBilling = billingFromLeadProjects(
          billingRes?.findBookingProject,
          projectId,
        );

        if (!projectId) {
          let next = fillInvoiceBillingFromProject(invoice, null, {
            projectAddress: invoice?.bookingInfo?.address,
            ...leadBilling,
          });
          if (
            bookingId &&
            !String(next?.bookingInfo?.fullName || '').trim()
          ) {
            const clientRes = await graphqlRequest(FIND_INVOICE_CLIENT, {
              id: bookingId,
            }).catch(() => ({}));
            const booking = clientRes?.findOneBooking || {};
            const clientAddress = [booking.address, booking.postcode]
              .map((part) => String(part || '').trim())
              .filter(Boolean)
              .join(', ');
            next = {
              ...next,
              bookingInfo: {
                ...(next.bookingInfo || {}),
                id: next.bookingInfo?.id || booking.id || bookingId,
                fullName: booking.fullName || '',
                email: booking.email || '',
                phone: booking.phone || '',
                address: clientAddress,
              },
            };
          }
          next = withTaskFacing(
            next,
            await facingIndexesFor(next, []),
          );
          if (!cancelled) setDisplayInvoice(next);
          return;
        }
        const [taskRes, projectRes] = await Promise.all([
          graphqlRequest(FIND_TASKS_BY_PROJECT, { projectId }).catch(() => ({})),
          graphqlRequest(FIND_PROJECT_SERVICES, { id: projectId }).catch(
            () => ({}),
          ),
        ]);
        const taskRows = Array.isArray(taskRes?.findTasksByProject)
          ? taskRes.findTasksByProject
          : [];
        let project = projectRes?.findOneProject || {};
        const rootId = project.seriesRootId;
        if (rootId) {
          const rootRes = await graphqlRequest(FIND_PROJECT_SERVICES, {
            id: rootId,
          }).catch(() => ({}));
          const root = rootRes?.findOneProject || {};
          project = {
            ...root,
            ...project,
            attention: project.attention || root.attention,
            projectAddress: project.projectAddress || root.projectAddress,
            deliveryAddress: project.deliveryAddress || root.deliveryAddress,
            purchaseOrderNumber:
              project.purchaseOrderNumber || root.purchaseOrderNumber,
            booking: project.booking || root.booking,
          };
        }
        const projectServices = project.projectServices || [];
        let next = ensureInvoiceHasBaseServices(invoice, projectServices);
        if (invoiceNeedsTaskHydration(next)) {
          next = hydrateInvoiceWithTasks(next, taskRows, projectServices);
        }
        next = keepSelectedProjectTasks(next, taskRows, projectServices);
        next = mergeVisitExtrasIntoInvoice(next, project.visitExtras);
        next = fillInvoiceBillingFromProject(next, project, leadBilling);
        if (
          bookingId &&
          !String(next?.bookingInfo?.fullName || '').trim()
        ) {
          const clientRes = await graphqlRequest(FIND_INVOICE_CLIENT, {
            id: bookingId,
          }).catch(() => ({}));
          const booking = clientRes?.findOneBooking || {};
          const clientAddress = [booking.address, booking.postcode]
            .map((part) => String(part || '').trim())
            .filter(Boolean)
            .join(', ');
          next = {
            ...next,
            bookingInfo: {
              ...(next.bookingInfo || {}),
              id: next.bookingInfo?.id || booking.id || bookingId,
              fullName: booking.fullName || next.bookingInfo?.fullName || '',
              email: booking.email || next.bookingInfo?.email || '',
              phone: booking.phone || next.bookingInfo?.phone || '',
              address: clientAddress || next.bookingInfo?.address || '',
            },
          };
        }
        next = withTaskFacing(
          next,
          await facingIndexesFor(next, projectServices),
        );
        if (!cancelled) setDisplayInvoice(next);
      } catch (err) {
        console.error('Failed to load invoice tasks:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [invoice, bookingIdProp, contextProjectId]);

  // If this invoice has no bank details, load company Settings → Payments defaults.
  useEffect(() => {
    const existing = invoice?.bankDetails || {};
    const hasBank = Boolean(
      existing.bankName ||
        existing.accountName ||
        existing.bsb ||
        existing.accountNumber ||
        existing.bankNumber ||
        existing.reference ||
        invoice?.companyInfo?.invoiceBankName ||
        invoice?.companyInfo?.invoiceAccountName ||
        invoice?.companyInfo?.invoiceBsb ||
        invoice?.companyInfo?.invoiceAccountNumber,
    );
    let cancelled = false;
    (async () => {
      try {
        const res = await graphqlRequest(GET_COMPANY_INVOICE_BANK);
        const c = res?.getMyProfile?.company;
        if (cancelled || !c) return;
        setCompanyProfile(c);
        if (!hasBank) {
          setCompanyBankFallback({
            bankName: String(c.invoiceBankName || '').trim(),
            accountName: String(c.invoiceAccountName || '').trim(),
            bsb: String(c.invoiceBsb || '').trim(),
            accountNumber: String(c.invoiceAccountNumber || '').trim(),
            reference: String(c.invoiceBankReference || '').trim(),
          });
        } else {
          setCompanyBankFallback(null);
        }
      } catch {
        // Non-fatal — company and bank sections fall back to the invoice snapshot.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [invoice]);

  const resolvedBookingId =
    bookingIdProp || displayInvoice?.bookingInfo?.id || '';

  const company = mergeCompany(displayInvoice?.companyInfo, companyProfile);
  const client = displayInvoice?.bookingInfo || {};
  const servicesBlock = displayInvoice?.services || {};
  const serviceList = Array.isArray(servicesBlock.data) ? servicesBlock.data : [];
  const serviceGroups = Array.isArray(servicesBlock.groups)
    ? servicesBlock.groups
    : [];
  const invoiceType = inferInvoiceType(displayInvoice);
  const lineSumFromGroups = serviceGroups.reduce((sum, g) => {
    const tasks = Array.isArray(g.tasks) ? g.tasks : [];
    const priced = tasks
      .map((t) => Number(t.price) || Number(t.total) || 0)
      .filter((p) => p > 0);
    const sameTaskPrice =
      priced.length > 1 && priced.every((p) => p === priced[0]);
    const byTask =
      g.pricingMode === 'task' ||
      (g.pricingMode !== 'service' &&
        priced.length >= 1 &&
        new Set(priced).size > 1);
    if (byTask) {
      return (
        sum +
        tasks.reduce(
          (s, t) => s + (Number(t.total) || Number(t.price) || 0),
          0,
        )
      );
    }
    return (
      sum +
      (Number(g.serviceTotal) ||
        Number(g.servicePrice) ||
        (sameTaskPrice ? priced[0] : 0) ||
        0)
    );
  }, 0);
  const lineSum =
    (serviceGroups.length
      ? lineSumFromGroups
      : serviceList.reduce((sum, item) => {
          if (item?.included || item?.priceSource === 'included') return sum;
          if (
            String(item?.priceSource || '') === 'visit-extra' &&
            (item?.taskId ||
              String(item?.id || '').endsWith('-task') ||
              item?.title ||
              item?.taskTitle)
          ) {
            return sum;
          }
          return sum + (Number(item.total) || 0);
        }, 0)) || 0;
  const storedGrand =
    Number(displayInvoice?.totalAmount) ||
    Number(servicesBlock.totalWithTax) ||
    0;
  const computed = calcInvoiceTotals(lineSum, invoiceType);
  const grandTotal =
    lineSum > 0 ? computed.grandTotal : storedGrand || computed.grandTotal;
  const gst =
    invoiceType === 'GST_EXCLUDED'
      ? 0
      : invoiceType === 'GST_INCLUDED'
        ? grandTotal / 11
        : computed.gst || Math.max(0, grandTotal - lineSum);
  const subtotal =
    invoiceType === 'GST_INCLUDED'
      ? grandTotal - gst
      : lineSum || Number(servicesBlock.totalPrice) || computed.subtotalExGst;
  const bankFromInvoice = displayInvoice?.bankDetails || {};
  const companyInfo = displayInvoice?.companyInfo || {};
  const bankHasValues = Boolean(
    bankFromInvoice.bankName ||
      bankFromInvoice.accountName ||
      bankFromInvoice.bsb ||
      bankFromInvoice.accountNumber ||
      bankFromInvoice.bankNumber ||
      bankFromInvoice.reference,
  );
  const bankFromCompanyInfo = {
    bankName: companyInfo.invoiceBankName || '',
    accountName: companyInfo.invoiceAccountName || '',
    bsb: companyInfo.invoiceBsb || '',
    accountNumber: companyInfo.invoiceAccountNumber || '',
    reference: companyInfo.invoiceBankReference || '',
  };
  const companyInfoHasBank = Boolean(
    bankFromCompanyInfo.bankName ||
      bankFromCompanyInfo.accountName ||
      bankFromCompanyInfo.bsb ||
      bankFromCompanyInfo.accountNumber ||
      bankFromCompanyInfo.reference,
  );
  const bank = bankHasValues
    ? bankFromInvoice
    : companyInfoHasBank
      ? bankFromCompanyInfo
      : companyBankFallback || {};
  const showBank = Boolean(
    bank.bankName ||
      bank.accountName ||
      bank.bsb ||
      bank.accountNumber ||
      bank.bankNumber ||
      bank.reference,
  );

  const invoiceNumber =
    displayInvoice?.invoiceNumber ||
    `INV-${String(resolvedBookingId).slice(0, 8).toUpperCase()}`;

  // Each invoice keeps the date it was created with. Opening or creating
  // another invoice must not move this one to today.
  const invoiceDate =
    displayInvoice?.issuedAt ||
    displayInvoice?.createdAt ||
    client.createdAt ||
    null;
  const dueDate = displayInvoice?.dueDate
    ? displayInvoice.dueDate
    : invoiceDate
      ? addDays(invoiceDate, 15)
      : null;
  const serviceDate = displayInvoice?.serviceDate || null;
  const serviceEndDate = displayInvoice?.serviceEndDate || null;

  const [status, setStatus] = useState(() => {
    const payment = String(invoice?.paymentStatus || 'unpaid').toLowerCase();
    if (payment === 'sent') return 'unpaid';
    return payment || 'unpaid';
  });
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const handleDownload = async () => {
    if (!slipRef.current) return;
    try {
      setDownloading(true);
      await html2pdf()
        .set({
          margin: [0.2, 0.2, 0.2, 0.2],
          filename: `Invoice-${invoiceNumber}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, scrollY: 0 },
          jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
        })
        .from(slipRef.current)
        .save();
    } catch (err) {
      console.error(err);
      toast.error('Failed to download invoice');
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // "PDF" from a document list: let billing / bank details settle, download, close.
  useEffect(() => {
    if (!autoDownload || autoDone.current) return undefined;
    autoDone.current = true;
    const timer = setTimeout(() => {
      const run = onPdf
        ? import('../utils/downloadElementPdf')
            .then(({ downloadElementPdf }) => {
              const name = `Invoice-${invoiceNumber}.pdf`;
              return downloadElementPdf(slipRef.current, name, { output: 'blob' }).then(
                (blob) => onPdf(blob, name),
              );
            })
            .catch((err) => {
              console.error(err);
              toast.error('Could not prepare the invoice PDF');
            })
        : handleDownload();
      void run.finally(() => onClose?.());
    }, 900);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDownload]);

  const updateStatus = async (newStatus) => {
    const invoiceId = invoice?.id;
    if (!invoiceId && !resolvedBookingId) {
      toast.error('Invoice not found.');
      return;
    }
    try {
      setLoading(true);
      if (invoiceId) {
        await graphqlRequest(UPDATE_PAYMENT_STATUS_BY_INVOICE, {
          invoiceId,
          paymentStatus: newStatus,
        });
      } else {
        // Legacy fallback when invoice row id is missing.
        await graphqlRequest(UPDATE_PAYMENT_STATUS_BY_BOOKING, {
          bookingId: resolvedBookingId,
          paymentStatus: newStatus,
        });
      }
      const normalized = newStatus.toLowerCase();
      setStatus(normalized);
      if (invoice) invoice.paymentStatus = normalized;
      onPaymentStatusChange?.(normalized);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Failed to update status.');
    } finally {
      setLoading(false);
    }
  };

  if (!invoice) {
    return createPortal(
      <div className="vi-modal-overlay" onClick={onClose} role="presentation">
        <div
          className="vi-modal-panel"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Invoice"
        >
          <div className="vi-modal-body">
            <div className="vi-empty">No invoice data available.</div>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      className="vi-modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      role="presentation"
    >
      <div
        className="vi-modal-panel"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Invoice"
      >
        <div className="vi-modal-header vi-no-print">
          <div>
            <h2 className="vi-modal-title">Invoice</h2>
            <p className="vi-modal-subtitle">
              {displayValue(company.companyName)}
              {invoiceNumber ? ` · ${invoiceNumber}` : ''}
            </p>
          </div>
          <div className="vi-modal-actions">
            <span className={`vi-status-chip ${status}`}>{status}</span>
            {typeof onEdit === 'function' && (
              <button
                type="button"
                className="vi-plain-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEdit();
                }}
                title="Edit invoice"
                aria-label="Edit invoice"
              >
                <FiEdit2 size={14} />
                Edit
              </button>
            )}
            <button
              type="button"
              className="vi-plain-btn"
              disabled={loading}
              onClick={() => updateStatus(PaymentStatus.PAID)}
            >
              Mark Paid
            </button>
            <button
              type="button"
              className="vi-plain-btn"
              disabled={loading}
              onClick={() => updateStatus(PaymentStatus.UNPAID)}
            >
              Mark Unpaid
            </button>
            <button
              type="button"
              className="vi-plain-btn"
              onClick={handleDownload}
              disabled={downloading}
            >
              <FiDownload size={12} />
              {downloading ? 'Downloading…' : 'Download PDF'}
            </button>
            <button type="button" className="vi-print-btn" onClick={handlePrint}>
              <FiPrinter size={12} /> Print
            </button>
            <button
              type="button"
              className="vi-close-btn"
              onClick={onClose}
              aria-label="Close"
            >
              <FiX size={18} />
            </button>
          </div>
        </div>

        <div className="vi-modal-body">
          <div className="ph-slip-card vi-slip-card" ref={slipRef}>
            <div className="ph-slip-header">
              <h3>INVOICE</h3>
              <div className="ph-slip-meta">
                <div>
                  <strong>Invoice No:</strong> {invoiceNumber}
                </div>
                {displayInvoice?.customTitle ? (
                  <div>
                    <strong>Subject:</strong> {displayInvoice.customTitle}
                  </div>
                ) : null}
                <div>
                  <strong>Invoice Date:</strong> {fmtDate(invoiceDate)}
                </div>
                <div>
                  <strong>Due Date:</strong> {fmtDate(dueDate)}
                </div>
                {displayInvoice?.paymentTerms ? (
                  <div>
                    <strong>Payment terms:</strong> {displayInvoice.paymentTerms}
                  </div>
                ) : null}
                {displayInvoice?.projectReference ? (
                  <div>
                    <strong>Project:</strong> {displayInvoice.projectReference}
                  </div>
                ) : null}
                {/* One-day service: a single date instead of From / To. */}
                {!serviceDate || !serviceEndDate || fmtDate(serviceEndDate) === fmtDate(serviceDate) ? (
                  <div>
                    <strong>Service Date:</strong> {fmtDate(serviceDate || serviceEndDate)}
                  </div>
                ) : (
                  <>
                    <div>
                      <strong>Service Date From:</strong> {fmtDate(serviceDate)}
                    </div>
                    <div>
                      <strong>Service Date To:</strong> {fmtDate(serviceEndDate)}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="ph-parties">
              <div className="ph-party">
                <h4>Company Details</h4>
                <p>
                  <strong>Name:</strong> {displayValue(company.companyName)}
                </p>
                <p>
                  <strong>ABN:</strong> {displayValue(company.abn)}
                </p>
                <p>
                  <strong>Address:</strong> {buildCompanyAddress(company)}
                </p>
                <p>
                  <strong>Phone:</strong>{' '}
                  {displayValue(company.companyPhone)}
                </p>
                <p>
                  <strong>Email:</strong>{' '}
                  {displayValue(
                    company.invoicingEmail || company.companyEmail,
                  )}
                </p>
              </div>
              <div className="ph-party">
                <h4>Client Details</h4>
                <p>
                  <strong>Name:</strong> {displayValue(client.fullName)}
                </p>
                <p>
                  <strong>Attention:</strong>{' '}
                  {displayValue(displayInvoice?.attention)}
                </p>
                <p>
                  <strong>Address:</strong> {displayValue(client.address)}
                </p>
                <p>
                  <strong>Project Address:</strong>{' '}
                  {displayValue(displayInvoice?.projectAddress)}
                </p>
                <p>
                  <strong>Delivery Address:</strong>{' '}
                  {displayValue(displayInvoice?.deliveryAddress)}
                </p>
                <p>
                  <strong>PO Number:</strong>{' '}
                  {displayValue(displayInvoice?.purchaseOrderNumber)}
                </p>
                <p>
                  <strong>Phone:</strong> {displayValue(client.phone)}
                </p>
                <p>
                  <strong>Email:</strong> {displayValue(client.email)}
                </p>
              </div>
            </div>

            <div className="ph-block-title">Service Details</div>
            {serviceGroups.length ? (
              <div className="vi-service-stack">
                {serviceGroups.map((group, gi) => {
                  const priced = groupPricing(group);
                  return (
                    <article
                      className="vi-service-card"
                      key={group.key || group.serviceId || gi}
                    >
                      <header>
                        <span>Service {gi + 1}</span>
                        <strong>{priced.name}</strong>
                      </header>
                      <dl className="vi-service-meta">
                        <div>
                          <dt>Service name</dt>
                          <dd>{displayValue(priced.name)}</dd>
                        </div>
                        <div>
                          <dt>Frequency</dt>
                          <dd>{priced.frequency}</dd>
                        </div>
                        <div>
                          <dt>Pricing</dt>
                          <dd>
                            {priced.byTask ? 'Price by task' : 'Service package'}
                          </dd>
                        </div>
                        {priced.byTask ? null : (
                          <div>
                            <dt>Discount</dt>
                            <dd>{priced.discount || 0}%</dd>
                          </div>
                        )}
                      </dl>
                      <h5>Operational tasks</h5>
                      {priced.tasks.length ? (
                        <div className="vi-task-wrap">
                          <table className="ph-detail-table vi-task-table">
                            <thead>
                              <tr>
                                <th>Task name</th>
                                <th>Description</th>
                                <th style={{ textAlign: 'right' }}>Price</th>
                                {priced.byTask ? (
                                  <th style={{ textAlign: 'right' }}>Disc</th>
                                ) : null}
                              </tr>
                            </thead>
                            <tbody>
                              {priced.tasks.map((task, ti) => {
                                const facing = taskFacing(task);
                                return (
                                  <tr key={task.taskId || task.key || `${gi}-${ti}`}>
                                    <td>
                                      {facing.groupName ? (
                                        <span className="vi-task-group">
                                          {facing.groupName}
                                        </span>
                                      ) : null}
                                      <strong>
                                        {displayValue(
                                          task.title || task.taskTitle || 'Task',
                                        )}
                                      </strong>
                                    </td>
                                    <td className="vi-scope-cell">
                                      {displayValue(facing.scope)}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                      {priced.byTask &&
                                      task.priceSource !== 'included' &&
                                      Number(task.total ?? task.price) > 0
                                        ? formatAUD(task.total ?? task.price)
                                        : 'Included'}
                                    </td>
                                    {priced.byTask ? (
                                      <td style={{ textAlign: 'right' }}>
                                        {task.priceSource === 'included'
                                          ? '—'
                                          : `${Number(task.discount || 0)}%`}
                                      </td>
                                    ) : null}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          <p className="vi-price-note">
                            {priced.byTask
                              ? 'Task prices below add up to this service subtotal.'
                              : 'These tasks are the job checklist. They are included in the service price and are not extra charges.'}
                          </p>
                        </div>
                      ) : (
                        <p className="vi-price-note">No operational tasks listed.</p>
                      )}
                      <p className="vi-service-total">
                        <span>Service total</span>
                        <strong>{formatAUD(priced.amount)}</strong>
                      </p>
                    </article>
                  );
                })}
              </div>
            ) : serviceList.length ? (
              <div className="vi-service-stack">
                {serviceList.map((item, index) => {
                  const included =
                    item.included || item.priceSource === 'included';
                  const facing = taskFacing(item);
                  return (
                    <article
                      className="vi-service-card"
                      key={item.id || item.taskId || index}
                    >
                      <header>
                        <span>Service {index + 1}</span>
                        <strong>
                          {displayValue(
                            item.taskTitle || item.title || item.service?.service,
                          )}
                        </strong>
                      </header>
                      <table className="ph-detail-table vi-task-table">
                        <thead>
                          <tr>
                            <th>Task name</th>
                            <th>Description</th>
                            <th style={{ textAlign: 'right' }}>Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>
                              {facing.groupName ? (
                                <span className="vi-task-group">{facing.groupName}</span>
                              ) : null}
                              <strong>
                                {displayValue(
                                  item.taskTitle || item.title || item.service?.service,
                                )}
                              </strong>
                            </td>
                            <td className="vi-scope-cell">{displayValue(facing.scope)}</td>
                            <td style={{ textAlign: 'right' }}>
                              {included ? 'Included' : formatAUD(item.total)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </article>
                  );
                })}
              </div>
            ) : (
              <p className="vi-price-note">No services listed.</p>
            )}
            <table className="ph-detail-table vi-totals-table">
              <tbody>
                <tr className="ph-total-row">
                  <td>
                    {invoiceType === 'GST_INCLUDED'
                      ? 'Subtotal (GST included)'
                      : invoiceType === 'GST_EXCLUDED'
                        ? 'Subtotal'
                        : 'Subtotal (ex. GST)'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {formatAUD(
                      invoiceType === 'GST_INCLUDED' ? grandTotal : subtotal,
                    )}
                  </td>
                </tr>
                <tr>
                  <td>{gstLineLabel(invoiceType)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAUD(gst)}</td>
                </tr>
              </tbody>
            </table>

            {Number(displayInvoice?.amountPaid) > 0 ? (
              <>
                <div className="ph-block-title">Payment Summary</div>
                <table className="ph-detail-table">
                  <tbody>
                    <tr>
                      <td>Amount Paid</td>
                      <td style={{ textAlign: 'right' }}>
                        {formatAUD(displayInvoice.amountPaid)}
                      </td>
                    </tr>
                    <tr>
                      <td>Amount Due</td>
                      <td style={{ textAlign: 'right' }}>
                        {formatAUD(
                          displayInvoice.amountDue ??
                            grandTotal -
                              Number(displayInvoice.amountPaid),
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </>
            ) : null}

            <div className="ph-net-box">
              <div>
                <span>
                  Grand Total ({grandTotalLabel(invoiceType)})
                </span>
                <span className="ph-net-sub">
                  {invoiceType === 'GST_INCLUDED'
                    ? `GST ${formatAUD(gst)} is already included in ${formatAUD(grandTotal)}`
                    : `Subtotal ${formatAUD(subtotal)} + GST ${formatAUD(gst)}`}
                </span>
              </div>
              <strong>{formatAUD(grandTotal)}</strong>
            </div>

            <p className="ph-slip-footnote ph-slip-footnote--due">
              {paymentDueSentence(invoiceDate, dueDate)}
            </p>

            {showBank ? (
              <>
                <div className="ph-block-title">Bank Details</div>
                <table className="ph-detail-table">
                  <tbody>
                    <tr>
                      <td>Bank name</td>
                      <td style={{ textAlign: 'right' }}>
                        {displayValue(bank.bankName)}
                      </td>
                    </tr>
                    <tr>
                      <td>Account name</td>
                      <td style={{ textAlign: 'right' }}>
                        {displayValue(bank.accountName)}
                      </td>
                    </tr>
                    <tr>
                      <td>BSB</td>
                      <td style={{ textAlign: 'right' }}>
                        {displayValue(bank.bsb)}
                      </td>
                    </tr>
                    <tr>
                      <td>Account number</td>
                      <td style={{ textAlign: 'right' }}>
                        {displayValue(
                          bank.accountNumber || bank.bankNumber,
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>Reference</td>
                      <td style={{ textAlign: 'right' }}>
                        {displayValue(bank.reference)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </>
            ) : null}

            <div className="ph-brand-footer">
              <p>
                © {new Date().getFullYear()}{' '}
                {displayValue(company.companyName)}. All rights reserved.
              </p>
              {company.invoicingEmail || company.companyEmail ? (
                <p>
                  Email:{' '}
                  {company.invoicingEmail || company.companyEmail}
                </p>
              ) : null}
              {company.companyPhone ? (
                <p>Phone: {company.companyPhone}</p>
              ) : null}
              <p style={{ color: '#9ca3af' }}>Powered by SAS Cleaning Suite</p>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
